import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiService } from '../../services/api';

const DiagnosticoPixCard = () => {
  const [locacaoId, setLocacaoId] = useState('');
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const buscar = async () => {
    const id = locacaoId.trim();
    if (!id) {
      setErro('Informe o ID da locação.');
      return;
    }
    setErro('');
    setResultado(null);
    setCarregando(true);
    try {
      const r = await apiService.api.get(`/sistema/diagnostico-pix/${id}`);
      setResultado(r.data);
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Falha ao consultar diagnóstico.');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Card data-testid="card-diagnostico-pix">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Search className="h-5 w-5 text-amber-500" />
          <span>Diagnóstico PIX por Locação</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Cole o <strong>ID (UUID)</strong> ou o <strong>número de registro</strong> (ex.: <code>1067</code>) de uma
          locação para descobrir <strong>exatamente qual PIX</strong> o sistema vai mandar no lembrete dela — e por quê.
          Útil quando o cliente recebe os dados bancários errados.
        </p>

        <div className="flex gap-2">
          <Input
            data-testid="input-diagnostico-locacao-id"
            value={locacaoId}
            onChange={(e) => setLocacaoId(e.target.value)}
            placeholder="UUID ou número da locação (ex.: 1067)"
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
          />
          <Button
            data-testid="btn-diagnostico-pix-buscar"
            onClick={buscar}
            disabled={carregando}
            className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
          >
            <Search className="h-4 w-4 mr-2" />
            {carregando ? 'Consultando...' : 'Consultar'}
          </Button>
        </div>

        {erro && (
          <div
            className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-200"
            data-testid="diagnostico-erro"
          >
            <AlertTriangle className="inline h-4 w-4 mr-1" /> {erro}
          </div>
        )}

        {resultado && (
          <div className="space-y-3 text-sm" data-testid="diagnostico-resultado">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-gray-500">Cliente:</span>{' '}
                <strong>{resultado.cliente_nome || '—'}</strong>
              </div>
              <div>
                <span className="text-gray-500">Moto:</span>{' '}
                <strong>{resultado.moto_placa || '—'}</strong>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>{' '}
                <Badge variant="outline">{resultado.status_locacao || '—'}</Badge>
              </div>
              <div>
                <span className="text-gray-500">Origem do PIX:</span>{' '}
                {resultado.origem_pix === 'sub_admin' ? (
                  <Badge className="bg-green-600">Sub-admin</Badge>
                ) : (
                  <Badge variant="secondary">Padrão (super-admin)</Badge>
                )}
              </div>
            </div>

            <div className="rounded-md border border-green-300 bg-green-50 dark:bg-green-900/20 p-3">
              <div className="font-semibold text-green-700 dark:text-green-300 mb-1 flex items-center">
                <CheckCircle2 className="h-4 w-4 mr-1" /> PIX que será enviado no lembrete
              </div>
              <div data-testid="pix-enviado-chave">
                <strong>Chave:</strong> {resultado.pix_que_sera_enviado?.chave_pix}
              </div>
              <div>
                <strong>Banco:</strong> {resultado.pix_que_sera_enviado?.nome_banco}
              </div>
              <div>
                <strong>Nome:</strong> {resultado.pix_que_sera_enviado?.nome_beneficiario}
              </div>
            </div>

            <details className="rounded-md border border-gray-200 dark:border-gray-700 p-2">
              <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-400">
                Compartilhamentos detectados
              </summary>
              <div className="mt-2 space-y-2 text-xs">
                <div>
                  <span className="text-gray-500">permissoes_subadmin (atual):</span>{' '}
                  {resultado.compartilhamentos?.permissoes_subadmin_usernames?.length
                    ? resultado.compartilhamentos.permissoes_subadmin_usernames.join(', ')
                    : <em>vazio</em>}
                </div>
                <div>
                  <span className="text-gray-500">locacoes_compartilhadas (legado):</span>{' '}
                  {resultado.compartilhamentos?.locacoes_compartilhadas_usuario_ids?.length
                    ? resultado.compartilhamentos.locacoes_compartilhadas_usuario_ids.join(', ')
                    : <em>vazio</em>}
                </div>
                {resultado.sub_admins_detalhados?.length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">Sub-admins relacionados:</div>
                    <ul className="space-y-1 pl-3">
                      {resultado.sub_admins_detalhados.map((s) => (
                        <li key={s.username}>
                          <strong>{s.username}</strong>{' '}
                          {s.tem_dados_cobranca
                            ? <Badge className="bg-green-600 text-[10px]">PIX próprio</Badge>
                            : <Badge variant="secondary" className="text-[10px]">sem PIX</Badge>}
                          {s.esta_em_permissoes_subadmin && (
                            <Badge variant="outline" className="text-[10px] ml-1">atual</Badge>
                          )}
                          {s.esta_em_locacoes_compartilhadas && (
                            <Badge variant="outline" className="text-[10px] ml-1">legado</Badge>
                          )}
                          {s.tem_dados_cobranca && (
                            <div className="pl-2 text-gray-500">
                              chave: {s.dados_cobranca?.chave_pix} · {s.dados_cobranca?.nome_banco}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Dump bruto: mostra o que está armazenado em cada sub-admin
                    pra permitir debug quando o vínculo esperado não é detectado. */}
                {resultado.compartilhamentos?.debug_permissoes_brutas?.length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">Debug — dump de permissoes_subadmin:</div>
                    <pre
                      className="text-[10px] bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-64"
                      data-testid="diagnostico-debug-dump"
                    >
                      {JSON.stringify(resultado.compartilhamentos.debug_permissoes_brutas, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </details>

            {resultado.origem_pix !== 'sub_admin' && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-amber-800 dark:text-amber-200"
                data-testid="diagnostico-aviso-padrao"
              >
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                Esta locação está usando o <strong>PIX padrão</strong>. Se você esperava o PIX de um sub-admin,
                verifique se ela está em <em>Usuários do Sistema → Permissões</em> do sub-admin certo
                e se o sub-admin tem PIX preenchido.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DiagnosticoPixCard;
